/* ============================================================
   Cart Page (main-cart) — behavior

   The page itself renders server-side from the real `cart` object
   (Liquid), so on first load everything is already correct with
   zero JS and zero requests — this script only progressively
   enhances it:
     - Quantity +/- and Remove use the Ajax Cart API
       (/cart/change.js) instead of a full page reload, updating
       just the affected row + totals.
     - The plain number input + "Update cart" button (the no-JS
       fallback, a real form submit to /cart/update) are hidden
       once JS confirms it can take over.
     - Recommended-product "add to cart" buttons use /cart/add.js,
       same pattern as the cart drawer's upsell buttons.
     - Fires `cart:updated` after any change so the floating cart
       pocket's badge count (cart-drawer.js) stays in sync with
       this page automatically.
   ============================================================ */

(function () {
  "use strict";

  var root, form, itemsEl, updateBtn, emptyEl, layoutEl, subtotalEl, countEl;
  var shippingBar, shippingText, shippingFill, upsellEl;
  var moneyFormatTemplate = "${{amount}}";
  var freeShippingThresholdCents = 0;
  var requestSeq = 0;

  /* Same store-configured money format as the cart drawer (see the
     currency fix in cart-drawer.js) — read from a data attribute
     rather than hardcoded, so AJAX-updated rows always match the
     currency the rest of the page (server-rendered via Liquid's
     `money` filter) already shows. */
  function moneyFormat(cents) {
    var amount = (Math.max(0, cents) / 100).toFixed(2);
    return moneyFormatTemplate.replace(/\{\{\s*amount\s*\}\}/, amount);
  }

  function escapeHtml(str) {
    var div = document.createElement("div");
    div.textContent = str || "";
    return div.innerHTML;
  }

  function init() {
    root = document.querySelector("[data-main-cart]");
    if (!root) return;

    form = root.querySelector("[data-main-cart-form]");
    itemsEl = root.querySelector("[data-cart-page-items]");
    updateBtn = root.querySelector("[data-cart-page-update]");
    emptyEl = root.querySelector("[data-cart-page-empty]");
    layoutEl = root.querySelector("[data-cart-page-layout]");
    subtotalEl = root.querySelector("[data-cart-page-subtotal]");
    countEl = root.querySelector("[data-cart-page-count]");
    shippingBar = root.querySelector("[data-cart-shipping-bar]");
    shippingText = root.querySelector("[data-cart-shipping-text]");
    shippingFill = root.querySelector("[data-cart-shipping-fill]");
    upsellEl = root.querySelector("[data-cart-page-upsell]");

    if (root.dataset.moneyFormat) {
      moneyFormatTemplate = root.dataset.moneyFormat;
    }
    if (root.dataset.freeShippingThreshold) {
      freeShippingThresholdCents = (parseFloat(root.dataset.freeShippingThreshold) || 0) * 100;
    }

    /* JS is working — hide the no-JS "Update cart" submit button,
       since qty changes now apply instantly without it. */
    if (updateBtn) updateBtn.hidden = true;

    bindRows();
    initUpsellButtons();

    /* Reflect the current cart on load, in case it was served from
       a cached page (back/forward cache, CDN cache) and is stale
       relative to what /cart.js actually has right now. */
    refreshFromServer();

    document.addEventListener("cart:updated", function (e) {
      /* Avoid re-fetching for the change WE just caused below —
         changeLine() already has the freshest cart and rendered it. */
      if (e.detail && e.detail.source === "main-cart") return;
      refreshFromServer();
    });
  }

  function refreshFromServer() {
    var thisRequest = ++requestSeq;
    fetch("/cart.js", { headers: { Accept: "application/json" }, cache: "no-store" })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        if (thisRequest !== requestSeq) return;
        render(cart);
      })
      .catch(function () {});
  }

  function bindRows() {
    if (!itemsEl) return;
    itemsEl.querySelectorAll("[data-cart-page-row]").forEach(bindRow);
  }

  function bindRow(row) {
    var line = row.getAttribute("data-line");
    var input = row.querySelector("[data-qty-input]");
    var decrease = row.querySelector("[data-qty-decrease]");
    var increase = row.querySelector("[data-qty-increase]");
    var removeBtns = row.querySelectorAll("[data-remove-item]");

    if (decrease) {
      decrease.addEventListener("click", function () {
        var next = Math.max(0, (parseInt(input.value, 10) || 0) - 1);
        changeLine(line, next, row);
      });
    }
    if (increase) {
      increase.addEventListener("click", function () {
        var next = (parseInt(input.value, 10) || 0) + 1;
        changeLine(line, next, row);
      });
    }
    if (input) {
      input.addEventListener("change", function () {
        var next = Math.max(0, parseInt(input.value, 10) || 0);
        changeLine(line, next, row);
      });
    }
    removeBtns.forEach(function (btn) {
      btn.addEventListener("click", function (e) {
        e.preventDefault();
        changeLine(line, 0, row);
      });
    });
  }

  function changeLine(line, quantity, row) {
    if (row) row.classList.add("is-updating");
    var thisRequest = ++requestSeq;

    fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ line: line, quantity: quantity })
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        if (thisRequest !== requestSeq) return; // a newer change has already superseded this one
        render(cart);
        document.dispatchEvent(
          new CustomEvent("cart:updated", { bubbles: true, detail: { source: "main-cart" } })
        );
      })
      .catch(function () {
        if (row) row.classList.remove("is-updating");
      });
  }

  /* Re-renders just the parts that can change: item rows, subtotal,
     heading count, empty-state visibility, and the shipping bar.
     Rebuilding the item list from scratch on every change keeps this
     in sync with line reordering/merging that /cart/change.js can do
     server-side (e.g. two lines merging into one). */
  function render(cart) {
    if (countEl) countEl.textContent = cart.item_count > 0 ? "(" + cart.item_count + ")" : "";
    updateShippingBar(cart.total_price, cart.item_count);

    if (!cart.items.length) {
      if (layoutEl) layoutEl.hidden = true;
      if (emptyEl) emptyEl.hidden = false;
      return;
    }
    if (layoutEl) layoutEl.hidden = false;
    if (emptyEl) emptyEl.hidden = true;
    if (subtotalEl) subtotalEl.textContent = moneyFormat(cart.total_price);

    if (!itemsEl) return;
    itemsEl.innerHTML = "";
    cart.items.forEach(function (item, index) {
      var line = index + 1;
      var row = document.createElement("div");
      row.className = "main-cart__item";
      row.setAttribute("data-cart-page-row", "");
      row.setAttribute("data-line", line);
      row.innerHTML =
        '<img class="main-cart__item-image" src="' +
        (item.image ? item.image + (item.image.indexOf("?") > -1 ? "&" : "?") + "width=200" : "") +
        '" alt="' +
        escapeHtml(item.product_title) +
        '" loading="lazy">' +
        '<div class="main-cart__item-info">' +
        '<a class="main-cart__item-title" href="' +
        item.url +
        '">' +
        escapeHtml(item.product_title) +
        "</a>" +
        (item.variant_title && item.variant_title !== "Default Title"
          ? '<p class="main-cart__item-variant">' + escapeHtml(item.variant_title) + "</p>"
          : "") +
        '<p class="main-cart__item-price-mobile">' +
        moneyFormat(item.final_line_price) +
        "</p>" +
        '<button type="button" class="main-cart__item-remove" data-remove-item>Remove</button>' +
        '<div class="main-cart__qty">' +
        '<button type="button" class="main-cart__qty-btn" data-qty-decrease aria-label="Decrease quantity">−</button>' +
        '<input type="number" class="main-cart__qty-input" data-qty-input min="0" value="' +
        item.quantity +
        '" aria-label="Quantity">' +
        '<button type="button" class="main-cart__qty-btn" data-qty-increase aria-label="Increase quantity">+</button>' +
        "</div>" +
        "</div>" +
        '<p class="main-cart__item-price">' +
        moneyFormat(item.final_line_price) +
        "</p>" +
        '<div class="main-cart__item-remove-desktop">' +
        '<button type="button" class="main-cart__item-remove" data-remove-item>Remove</button>' +
        "</div>";
      itemsEl.appendChild(row);
      bindRow(row);
    });
  }

  function updateShippingBar(totalCents, itemCount) {
    if (!shippingBar || freeShippingThresholdCents <= 0) return;

    if (!itemCount) {
      shippingBar.hidden = true;
      return;
    }
    shippingBar.hidden = false;

    var remaining = freeShippingThresholdCents - totalCents;
    var percent = Math.max(0, Math.min(100, (totalCents / freeShippingThresholdCents) * 100));

    if (shippingFill) shippingFill.style.width = percent + "%";
    if (shippingText) {
      if (remaining > 0) {
        shippingText.innerHTML = "You're " + moneyFormat(remaining) + " away from <strong>FREE SHIPPING</strong>!";
      } else {
        shippingText.innerHTML = "You've unlocked <strong>FREE SHIPPING</strong>!";
      }
    }
  }

  /* ---------- Recommended products ("our customer's favorites") ----------
     Same shared settings + markup pattern as the cart drawer's
     upsell section, so the two look and behave identically. */
  function initUpsellButtons() {
    if (!upsellEl) return;
    upsellEl.querySelectorAll("[data-upsell-add]").forEach(function (btn) {
      btn.addEventListener("click", function () {
        if (btn.disabled || btn.classList.contains("is-loading")) return;
        var variantId = btn.getAttribute("data-variant-id");
        if (!variantId) return;

        btn.classList.add("is-loading");
        fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
        })
          .then(function (r) {
            return r.json();
          })
          .then(function () {
            btn.classList.remove("is-loading");
            document.dispatchEvent(new CustomEvent("cart:updated", { bubbles: true }));
            refreshFromServer();
          })
          .catch(function () {
            btn.classList.remove("is-loading");
          });
      });
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();